import React, { useEffect, useRef, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import { useSettings } from '../contexts/SettingsContext';
import { setMetadata } from '../utils/encryptedStorage';

/**
 * GoogleSignIn Component
 * 
 * Renders a "Sign in with Google" button that uses Google Identity Services.
 * On successful Google authentication, sends credentials to backend for login/signup.
 * 
 * @param {function} onError - Callback for error messages
 * @param {function} onSuccess - Callback for success messages
 * @param {function} setIsLoading - Callback to set loading state
 * @param {boolean} isSignup - Whether this is being used on signup page (for different handling)
 */
const GoogleSignIn = ({ onError, onSuccess, setIsLoading, isSignup = false, candidate = false }) => {
  const { settings } = useSettings();
  const navigate = useNavigate();
  const hiddenButtonRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Environment variables
  const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const SECRET_KEY = process.env.REACT_APP_SECRETKEY;
  const isGoogleConfigured = Boolean(CLIENT_ID);

  // Get theme colors
  const primaryColor = settings?.theme?.colors?.primary || '#0d9488';

  useEffect(() => {
    if (!CLIENT_ID) {
      console.warn('Google Client ID not configured');
      onError?.('Google Sign-In is not configured. Set REACT_APP_GOOGLE_CLIENT_ID in ux/.env and restart the frontend.');
      return;
    }

    const loadGoogleScript = () => {
      // Check if script is already loaded
      if (window.google?.accounts?.id) {
        initializeGoogleSignIn();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogleSignIn;
      script.onerror = () => {
        console.error('Failed to load Google Sign-In script');
        onError?.('Failed to load Google Sign-In');
      };
      document.body.appendChild(script);
    };

    const initializeGoogleSignIn = () => {
      if (!window.google?.accounts?.id) {
        console.error('Google Identity Services not available');
        return;
      }

      try {
        window.google.accounts.id.initialize({
          client_id: CLIENT_ID,
          callback: handleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
        });

        // Render hidden button for programmatic click
        window.google.accounts.id.renderButton(
          document.getElementById('google-signin-hidden'),
          { theme: 'outline', size: 'large', width: 250 }
        );

        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to initialize Google Sign-In:', error);
        onError?.('Failed to initialize Google Sign-In');
      }
    };

    loadGoogleScript();
  }, [CLIENT_ID]);

  /**
   * Handle Google credential response
   * Decodes the JWT and sends to backend for authentication
   */
  const handleCredentialResponse = async (response) => {
    if (!response?.credential) {
      onError?.('No credential received from Google');
      return;
    }

    try {
      setIsLoading?.(true);

      // Decode the JWT token to extract user info
      const decodedToken = jwtDecode(response.credential);
      console.log('Google user info:', decodedToken.email);

      // Extract user details from the token
      const { name, email, picture } = decodedToken;

      // Prepare signIn data object
      const signInData = {
        email,
        googleAuth: true,
        name,
        picture,
        isSignup, // Let backend know if this is a signup attempt
        candidate, // Pass role flag so backend uses correct table
      };

      // Encrypt the signIn data
      const encryptedData = CryptoJS.AES.encrypt(
        JSON.stringify(signInData),
        SECRET_KEY
      ).toString();

      // Send login request to backend
      const loginResponse = await fetch(`${API_BASE_URL}/auth/google-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: encryptedData }),
      });

      const data = await loginResponse.json();
      console.log('Google login result:', data.message);

      if (!loginResponse.ok) {
        throw new Error(data.error || 'Google Sign-In failed');
      }

      if (data.token) {
        // Decrypt user data from backend (same as LoginPage)
        let userData = null;
        if (data.data) {
          try {
            if (typeof data.data === 'string') {
              // Decrypt encrypted data from backend
              const bytes = CryptoJS.AES.decrypt(data.data, SECRET_KEY);
              const decrypted = bytes.toString(CryptoJS.enc.Utf8);
              userData = JSON.parse(decrypted);
            } else {
              // Fallback: if backend sends plain object
              userData = data.data;
            }
          } catch (err) {
            console.error('Failed to decrypt Google login response data:', err);
            onError?.('Failed to decrypt server response');
            return;
          }
        }

        console.log('Decrypted user data:', userData?.name, userData?.email);

        // Create metadata object with token
        const metadata = {
          uid: userData?.uid || userData?.id || null,
          email: userData?.email || '',
          role_id: userData?.role_id || 1,
          phone_no: userData?.phone_no || '',
          name: userData?.name || '',
          designation: userData?.designation || null,
          profile: userData?.profile || null,
          cid: userData?.cid || null,
          created_at: userData?.created_at || null,
          created_by: userData?.created_by || null,
          status: userData?.status ?? true,
          notification: userData?.notification || 'false',
          candidate_id: userData?.candidate_id || null,
          refresh_token: userData?.refresh_token || null,
          passcode: userData?.passcode || null,
          subscription_id: userData?.subscription_id || null,
          fitment_level: userData?.fitment_level || 'RELAXED',
          company_name: userData?.company_name || null,
          plan_id: userData?.plan_id || null,
          token: data.token, // Include token in metadata
        };

        // Store encrypted metadata using the same utility as LoginPage
        setMetadata(metadata);

        // Also store token separately for easy access
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('token', data.token);
        localStorage.setItem('token_type', 'HR');

        // Keep backward compatibility - also store user separately
        if (userData) {
          localStorage.setItem('user', JSON.stringify(userData));
        }

        // Check if user has company (cid). If not, redirect to workspace setup
        // New users without a company need to create one first
        if (!userData.cid || userData.cid === '' || userData.cid === null) {
          onSuccess?.('Account created successfully');
          navigate('/auth/workspace-setup');
          return;
        }

        onSuccess?.('Signed in successfully');
        navigate('/dashboard');
      } else {
        throw new Error(data.error || 'No token received');
      }
    } catch (error) {
      console.error('Error during Google signIn:', error.message);
      onError?.(error.message || 'Google Sign-In failed');
    } finally {
      setIsLoading?.(false);
    }
  };

  /**
   * Trigger the hidden Google Sign-In button
   */
  const handleClick = () => {
    const hiddenDiv = document.getElementById('google-signin-hidden');
    if (hiddenDiv) {
      const button = hiddenDiv.querySelector(
        '[role="button"], .nsm7Bb-HzV7m-LgbsSe, button'
      );
      if (button) {
        button.click();
      } else {
        // Fallback: try using Google One Tap
        window.google?.accounts?.id?.prompt?.();
      }
    }
  };

  return (
    <>
      {/* Custom styled button */}
      <button
        type="button"
        onClick={isGoogleConfigured ? handleClick : undefined}
        disabled={!isGoogleConfigured || !isInitialized}
        className={`
          w-full flex items-center justify-center gap-3 
          px-4 py-3 
          border border-gray-300 dark:border-gray-600 
          rounded-2xl
          bg-white dark:bg-gray-800 
          text-gray-700 dark:text-gray-200 
          font-medium text-sm
          transition-all duration-200
          hover:bg-gray-50 dark:hover:bg-gray-700
          hover:border-gray-400 dark:hover:border-gray-500
          focus:outline-none focus:ring-2 focus:ring-offset-2
          disabled:opacity-50 disabled:cursor-not-allowed
        `}
        style={{ 
          '--tw-ring-color': primaryColor,
        }}
      >
        {/* Google Icon */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 48 48"
          width="20"
          height="20"
        >
          <path
            fill="#FFC107"
            d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
          />
          <path
            fill="#FF3D00"
            d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
          />
          <path
            fill="#4CAF50"
            d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
          />
          <path
            fill="#1976D2"
            d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
          />
        </svg>
        <span>{isGoogleConfigured ? 'Sign in with Google' : 'Google Sign-In unavailable'}</span>
      </button>

      {!isGoogleConfigured && (
        <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
          Set <code>REACT_APP_GOOGLE_CLIENT_ID</code> in <code>ux/.env</code> and restart the frontend.
        </p>
      )}

      {/* Hidden Google Sign-In button */}
      <div
        ref={hiddenButtonRef}
        id="google-signin-hidden"
        style={{ display: 'none', position: 'absolute', visibility: 'hidden' }}
      />
    </>
  );
};

export default GoogleSignIn;
